// Stream — small time-series line chart for live CdA history.
// Auto-scales Y to fit the data, fills its parent container, and draws
// with theme colors. Replaces the earlier hardcoded-domain placeholder.

import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { colors } from '../theme';

interface DataPoint {
  date: Date;
  value: number;
}

interface StreamProps {
  data: DataPoint[];
  // Kept for backward compat; unused — we fill the parent 100%/100%.
  width?: number | string;
}

const VIEW_W = 600;
const VIEW_H = 200;
const MARGIN = { top: 14, right: 10, bottom: 22, left: 10 };

const Stream: React.FC<StreamProps> = ({ data }) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    if (!data || data.length < 2) {
      // Not enough data yet — draw a subtle placeholder text
      svg
        .append('text')
        .attr('x', VIEW_W / 2)
        .attr('y', VIEW_H / 2)
        .attr('text-anchor', 'middle')
        .attr('fill', colors.textFaint)
        .attr('font-family', '"JetBrains Mono", monospace')
        .attr('font-size', 11)
        .attr('letter-spacing', '0.1em')
        .text('COLLECTING DATA…');
      return;
    }

    // Auto-scale Y to the data's actual range, with a small margin so the
    // line doesn't touch the edges.
    const [minY, maxY] = d3.extent(data, (d) => d.value) as [number, number];
    const pad = Math.max((maxY - minY) * 0.15, 0.005);
    const yDomain: [number, number] = [minY - pad, maxY + pad];

    const xScale = d3
      .scaleTime()
      .domain(d3.extent(data, (d) => d.date) as [Date, Date])
      .range([MARGIN.left, VIEW_W - MARGIN.right]);

    const yScale = d3
      .scaleLinear()
      .domain(yDomain)
      .range([VIEW_H - MARGIN.bottom, MARGIN.top]);

    // Gradient fill under the line
    const defs = svg.append('defs');
    const gradient = defs
      .append('linearGradient')
      .attr('id', 'stream-gradient')
      .attr('x1', '0')
      .attr('y1', '0')
      .attr('x2', '0')
      .attr('y2', '1');
    gradient
      .append('stop')
      .attr('offset', '0%')
      .attr('stop-color', colors.brand)
      .attr('stop-opacity', 0.35);
    gradient
      .append('stop')
      .attr('offset', '100%')
      .attr('stop-color', colors.brand)
      .attr('stop-opacity', 0);

    // Area fill
    const area = d3
      .area<DataPoint>()
      .defined((d) => !isNaN(d.value))
      .x((d) => xScale(d.date))
      .y0(VIEW_H - MARGIN.bottom)
      .y1((d) => yScale(d.value))
      .curve(d3.curveMonotoneX);

    svg
      .append('path')
      .datum(data)
      .attr('fill', 'url(#stream-gradient)')
      .attr('d', area);

    // Line on top
    const line = d3
      .line<DataPoint>()
      .defined((d) => !isNaN(d.value))
      .x((d) => xScale(d.date))
      .y((d) => yScale(d.value))
      .curve(d3.curveMonotoneX);

    svg
      .append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', colors.brand)
      .attr('stroke-width', 2)
      .attr('stroke-linecap', 'round')
      .attr('stroke-linejoin', 'round')
      .attr('d', line);

    // Latest value dot
    const last = data[data.length - 1];
    svg
      .append('circle')
      .attr('cx', xScale(last.date))
      .attr('cy', yScale(last.value))
      .attr('r', 3.5)
      .attr('fill', colors.brand)
      .attr('stroke', colors.bg)
      .attr('stroke-width', 1.5);

    // Min / max labels on the right edge so users know the range
    svg
      .append('text')
      .attr('x', VIEW_W - MARGIN.right)
      .attr('y', MARGIN.top + 2)
      .attr('text-anchor', 'end')
      .attr('dominant-baseline', 'hanging')
      .attr('fill', colors.textFaint)
      .attr('font-family', '"JetBrains Mono", monospace')
      .attr('font-size', 10)
      .text(yDomain[1].toFixed(3));
    svg
      .append('text')
      .attr('x', VIEW_W - MARGIN.right)
      .attr('y', VIEW_H - MARGIN.bottom - 2)
      .attr('text-anchor', 'end')
      .attr('fill', colors.textFaint)
      .attr('font-family', '"JetBrains Mono", monospace')
      .attr('font-size', 10)
      .text(yDomain[0].toFixed(3));
  }, [data]);

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="none"
      style={{
        width: '100%',
        height: '100%',
        display: 'block',
      }}
    />
  );
};

export default Stream;
